import { MediumTitle, SmallUppercase, BackButton } from '@/components/investors/shared';
import { InvestorsPageTemplate } from '@/pageTemplate/InvestorsPageTemplage';
import { Flex } from '@lemonade-hq/cdk';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { SeoProps } from '@/components/shared/SEO';
import { Client } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import styled from 'styled-components';
import { LeftArrowIcon } from '@lemonade-hq/nuis';
import { fetchWithRetry } from '@/utils/fetchWithRetry';

const ArticleTitle = styled(MediumTitle)`
  text-align: left;
  margin-bottom: 10px;
`;

const ArticleDate = styled(SmallUppercase)`
  text-align: left;
`;

export type News = {
  id: string;
  date: string;
  title: string;
  content: string;
  year: number;
  slug: string;
};

const getPaths = async (): Promise<{ params: { slug: string } }[]> => {
  const { data } = await Client.get<{ data: string[] }>(`${process.env.NEXT_PUBLIC_INVESTORS_URL}/news-slugs`);
  return data.map(slug => ({
    params: { slug },
  }));
};

export const getNews = async (): Promise<{ data: News[] }> => {
  try {
    return await fetchWithRetry<{ data: News[] }>(`${process.env.NEXT_PUBLIC_INVESTORS_URL}/news`);
  } catch (err) {
    console.error('feeds error', err);
    return { data: [] };
  }
};

export const getStaticPaths: GetStaticPaths = (async () => {
  // this will avoid generating pages in build time on development | staging
  if (process.env.STAGE === 'development' || process.env.STAGE === 'staging' || process.env.STAGE == null) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const paths = await getPaths();

  return {
    paths,
    fallback: 'blocking',
  };
}) satisfies GetStaticPaths;

export const getStaticProps: GetStaticProps = async () => {
  const news = await getNews();
  return {
    props: {
      news: news.data,
    },
    revalidate: 10, // 10 seconds, we need this to be wired as much as possible to the data source
  };
};

const MAX_WIDTH = 600;

const seo = {
  title: 'Lemonade Inc. - News',
  description: '',
} as SeoProps;

export default function NewsPage({ news }: { news: News[] }) {
  const router = useRouter();
  const slug = router.query.slug as string;
  seo.description =
    slug === 'lemonade-1-billion-in-premiums'
      ? 'Learn about Lemonade’s milestone of surpassing $1 billion in premiums, reflecting our growth, innovation, and impact within the insurance sector.'
      : '';
  const article = useMemo(() => news.find(newsArticle => newsArticle.slug === slug), [slug, news]);

  if (!article) {
    return <div>Article not found</div>;
  }

  return (
    <InvestorsPageTemplate seo={{ ...seo, title: `${seo.title} - ${article?.title}` }}>
      <Flex
        flexDirection="column"
        gap="20px"
        width="100%"
        maxWidth={MAX_WIDTH}
        textAlign="left"
        px={[20, 20, 0]}
        as="article"
      >
        <BackButton onClick={() => router.back()}>
          <LeftArrowIcon colorOverride="#B7B7B7" />
          Back
        </BackButton>
        <Flex flexDirection="column" gap="4px" mb="20px">
          <ArticleTitle>{parse(article?.title)}</ArticleTitle>
          <ArticleDate>{article?.date}</ArticleDate>
        </Flex>
        <Flex gap="20px" flexDirection="column" lineHeight="145%">
          {parse(article?.content)}
        </Flex>
      </Flex>
    </InvestorsPageTemplate>
  );
}
